"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, MapPin, Clock, ShieldAlert, Send, CircleCheck as CheckCircle2, Loader, Navigation, Check } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface ServiceLocation {
  city: string;
  county: string;
  eta: string;
  status: string;
}
const serviceLocations: ServiceLocation[] = [{
  city: "Jonesboro",
  county: "Clayton County",
  eta: "15-30 Mins",
  status: "Primary Base"
}, {
  city: "Morrow",
  county: "Clayton County",
  eta: "20-35 Mins",
  status: "Active Coverage"
}, {
  city: "Riverdale",
  county: "Clayton County",
  eta: "20-40 Mins",
  status: "Active Coverage"
}, {
  city: "Forest Park",
  county: "Clayton County",
  eta: "25-45 Mins",
  status: "Active Coverage"
}, {
  city: "Stockbridge",
  county: "Henry County",
  eta: "25-45 Mins",
  status: "Active Coverage"
}, {
  city: "Fayetteville",
  county: "Fayette County",
  eta: "30-50 Mins",
  status: "Active Coverage"
}, {
  city: "Lovejoy",
  county: "Clayton County",
  eta: "20-35 Mins",
  status: "Active Coverage"
}, {
  city: "Lake City",
  county: "Clayton County",
  eta: "20-40 Mins",
  status: "Active Coverage"
}];
const highlights = ["Zero extra charges for late night or weekend calls", "Fully stocked mobile service vans dispatched immediately", "Licensed, insured, and background-checked master plumbers", "Upfront pricing before work begins"];
function EmergencyRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card rounded-xl border border-border">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-xl font-semibold font-default text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground text-sm max-w-sm mb-6">
          {successMessage || "We have received your emergency request and our on-call dispatcher will call your number immediately."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
          <a href="tel:4708479076" className="w-full">
            <Button className="w-full" variant="default">
              <PhoneCall className="mr-2 size-4" />
              Call Dispatch
            </Button>
          </a>
          <Button onClick={resetForm} variant="outline" className="w-full">
            Submit Another
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <input type="hidden" name="source_page" value="Drain Problems" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required for dispatch",
          pattern: {
            value: /^[0-9+()\s-]{7,20}$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(770) 555-0199" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="city_location" rules={{
          required: "Service city is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Service City / Zip</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Jonesboro, 30236" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="urgency" rules={{
          required: "Please select an urgency level"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Urgency Level</FormLabel>
                <FormControl>
                  <select className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground" {...field}>
                    <option value="Immediate Emergency" className="bg-card text-foreground">Immediate Emergency</option>
                    <option value="Within 24 Hours" className="bg-card text-foreground">Within 24 Hours</option>
                    <option value="This Week" className="bg-card text-foreground">This Week</option>
                    <option value="Just Exploring Options" className="bg-card text-foreground">Just Exploring Options</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="issue_description" rules={{
        required: "Please briefly describe your plumbing issue",
        minLength: {
          value: 6,
          message: "Please provide a little more detail"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Plumbing Problem Description</FormLabel>
              <FormControl>
                <Textarea rows={3} placeholder="Tell us what is leaking, backed up, or broken..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" rules={{
        required: "You must agree to receive communications to submit this request"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <div className="flex items-start gap-2 pt-1">
                <FormControl>
                  <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-0.5 size-4 rounded border-border text-primary focus:ring-accent" />
                </FormControl>
                <FormLabel className="text-xs text-muted-foreground font-normal leading-snug cursor-pointer">
                  I consent to receive phone calls and text messages from Jonesboro Emergency Plumber regarding my service request. Message and data rates may apply.
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
            {submitError}
          </div>}

        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Dispatching Request...
            </> : <>
              <Send className="mr-2 size-4" />
              Request Immediate Response
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ServiceContact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="serviceareacontact" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border-border">
            Coverage & Fast Dispatch
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-4">
            Jonesboro Service Area & Emergency Dispatch
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Fast, reliable 24-hour plumbing response across Jonesboro, Clayton County, and neighboring communities. Call our dispatch center directly or submit your service request below.
          </p>
        </div>

        {/* Emergency Call Banner */}
        <div className="mb-12 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 shadow-lg">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold">
                <ShieldAlert className="size-7" />
              </div>
              <div>
                <span className="text-accent text-sm font-semibold tracking-wider uppercase block">
                  Plumbing Emergency Right Now?
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold font-default">
                  Speak Directly With an On-Call Plumber
                </h3>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 text-base font-bold shadow-md">
                  <PhoneCall className="mr-2 size-5" />
                  (470) 847-9076
                </Button>
              </a>
              <Link to="/contact/" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  More Contact Info
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Main Grid: Coverage List & Contact Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Service Areas & Info */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <Navigation className="size-4" />
                  <span>Coverage Zones</span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Where We Respond Across Metro South Atlanta
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Our plumbing units are strategically positioned around major roadways for rapid arrival.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceLocations.map((location, locationIndex) => <motion.div key={location.city} data-index={locationIndex} initial={shouldReduceMotion ? false : {
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: locationIndex * 0.05
                }} className="p-3.5 rounded-lg border border-border bg-background flex items-center justify-between hover:border-primary/50 transition-colors">
                      <div className="flex items-start gap-2.5">
                        <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm text-foreground">{location.city}</p>
                          <p className="text-xs text-muted-foreground">{location.county}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-md bg-secondary text-secondary-foreground">
                          {location.eta}
                        </span>
                      </div>
                    </motion.div>)}
                </div>

                <Separator className="bg-border" />

                <div>
                  <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3">
                    Why Jonesboro Homeowners Trust Us
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {highlights.map((highlight, highlightIndex) => <div key={highlightIndex} data-index={highlightIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{highlight}</span>
                      </div>)}
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Clock className="size-5 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Standard & Emergency Hours</p>
                      <p className="text-xs text-muted-foreground">Dispatch operating 24 hours a day, 7 days a week, 365 days a year</p>
                    </div>
                  </div>
                  <Link to="/service-areas/">
                    <Button variant="secondary" size="sm" className="whitespace-nowrap">
                      Full Coverage Details
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Fast Service Request Form */}
          <div className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-md">
              <CardHeader className="pb-4">
                <Badge variant="outline" className="w-fit text-xs font-medium border-primary/30 text-primary bg-primary/5">
                  Online Dispatch
                </Badge>
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Send Service Request
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Fill in your details for quick callback and immediate technician scheduling.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="drain_cleaning_service_form" sectionName="ServiceAreaContact" defaultValues={{
                full_name: "",
                phone: "",
                city_location: "",
                urgency: "Immediate Emergency",
                issue_description: "",
                consent: false,
                source_page: "Drain Problems"
              }} validationRules={{
                full_name: {
                  required: true,
                  minLength: 2
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                city_location: {
                  required: true
                },
                urgency: {
                  required: true
                },
                issue_description: {
                  required: true,
                  minLength: 6
                },
                consent: {
                  required: true
                }
              }} submitText="Request Immediate Response" successMessage="Thank you! Our on-call plumber in Jonesboro will contact you right away.">
                  <EmergencyRequestForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}